import type { DeprecatedSystemProps } from 'src/types/DeprecatedSystemProps';
import { default as MuiBox } from '@mui/material/Box';
import type { BoxOwnProps, BoxTypeMap as MuiBoxTypeMap } from '@mui/system';
import type { Theme as SystemTheme } from '@mui/material/styles';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { OverrideProps } from '@mui/types';
import { ExtendTypeMap } from 'src/utils/forwardRefToOverridableComponent';

export type BoxTypeMap<
  AdditionalProps = unknown,
  RootComponent extends React.ElementType = MuiBoxTypeMap['defaultComponent'],
  Theme extends object = SystemTheme
> = ExtendTypeMap<
  {
    props: AdditionalProps & BoxOwnProps<Theme> & DeprecatedSystemProps<Theme>;
    defaultComponent: RootComponent;
  },
  AdditionalProps,
  RootComponent
>;

export type BoxProps<
  RootComponent extends React.ElementType = MuiBoxTypeMap['defaultComponent'],
  AdditionalProps = unknown
> = OverrideProps<BoxTypeMap<AdditionalProps, RootComponent>, RootComponent> & {
  component?: React.ElementType;
};

/**
 * The Box component serves as a wrapper component for CSS utility needs.
 *
 * Demos:
 *
 * - [Box](https://unify.workiva.org/components/developer-utilities/box)
 *
 * API:
 *
 * - [Box API](https://unify.workiva.org/components/developer-utilities/box?tab=props)
 */
export const Box: OverridableComponent<BoxTypeMap> = MuiBox;
